import clsx from "clsx";
import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import { groupAccessIdFromGroupId } from "../../../../shared/access-id";

import { StudentOptionsMenu } from "components/materials/page/groups/StudentOptionsMenu";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
} from "components/materials/page/groups/helpers";
import { isUserOnline, userActivityIcons } from "components/materials/section/helpers";
import { PageType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { LegacyButton } from "mds/components/LegacyButton";
import { ListItem } from "mds/components/ListItem";
import { LongFormPencilIcon, PersonSingleIcon, TrashDeleteIcon } from "mds/icons";
import { useAccessIdHasData } from "sharedb/ShareDBDoc";
import { storeApi, useAppSelector } from "store/index";
import {
  NestedPageGroupType,
  determineActivityStatus,
  selectActivityStatus,
} from "store/selectors";
import { sortByFirstName } from "store/selectors/sorting";
import { GroupInteractionModeType } from "utils/page-utils";
import { getUserFullName } from "utils/user-utils";

interface PageGroupCardProps {
  pageGroup: NestedPageGroupType;
  pageGroups: NestedPageGroupType[];
  number: number;
  totalPageGroups: number;
  page: PageType;
  onRemove?: (studentSectionUserId: string) => void;
  moveSectionUserLocally: (
    item: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    dropContainerId: string,
  ) => void;
  onDropSectionUser: (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    sourceContainerId: string,
  ) => void;
  canDeleteGroup: boolean;
}
export const PageGroupCard: FC<PageGroupCardProps> = ({
  pageGroup,
  pageGroups,
  number,
  totalPageGroups,
  page,
  onRemove,
  moveSectionUserLocally,
  onDropSectionUser,
  canDeleteGroup,
}) => {
  const groupAccessId = groupAccessIdFromGroupId(pageGroup.id);
  const hasData = useAccessIdHasData(page?.worksheet_id, groupAccessId);
  const userActivity = useAppSelector(selectActivityStatus);
  // We only show ready users in the group card
  const draggablePageGroupUsers: DraggablePageGroupUserType[] = pageGroup.page_group_users
    .filter((pageGroupUser) => pageGroupUser.is_ready)
    .toSorted(sortByFirstName)
    .map((pageGroupUser, index) => ({
      ...pageGroupUser,
      order: index,
    }));

  const isDeleteGroupEnabled = totalPageGroups > 2;

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const confirmDeleteGroup = () => {
    if (hasData) {
      openConfirmationDialog();
    } else {
      storeApi.page_groups.destroy(pageGroup.id);
    }
  };

  return (
    <>
      <DragAndDrop
        className="w-full"
        dragItemKind="section_user"
        dropContainerId={pageGroup.id}
        droppable
        onDraggedItemHover={moveSectionUserLocally}
      >
        <Card className="flex w-full flex-col flex-wrap" data-testid={`group-card-${pageGroup.id}`}>
          <div className="mb-1 flex w-full justify-between">
            <div className="h3 ml-2 truncate">{t("common.group_title", { number })}</div>
            <div className="flex gap-1">
              {draggablePageGroupUsers.length === 0 ? null : (
                <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
                  <PersonSingleIcon />
                  {draggablePageGroupUsers.length}
                </div>
              )}
              {canDeleteGroup && (
                <LegacyButton
                  disabled={!isDeleteGroupEnabled}
                  eventName="Group settings - Delete group"
                  kind="secondary"
                  size="xs"
                  title={
                    isDeleteGroupEnabled
                      ? t("group_configuration_card.tooltip.delete_group")
                      : undefined
                  }
                  iconOnly
                  onClick={confirmDeleteGroup}
                >
                  <TrashDeleteIcon />
                </LegacyButton>
              )}
            </div>
          </div>
          {draggablePageGroupUsers.length === 0 ? (
            <div className="body-s ml-2 mt-1 text-black-tint-55">
              <Trans
                i18nKey={`group_configuration_card.empty_group_tip.${
                  page.interaction_mode as GroupInteractionModeType
                }`}
                t={t}
              >
                Click <b>Distribute</b> or drag students here
              </Trans>
            </div>
          ) : (
            draggablePageGroupUsers.map((pageGroupUser) => {
              const userActivityStatus = userActivity[pageGroupUser.user_id];
              const userActivePageId = userActivityStatus?.pageId;
              const userActivityType = determineActivityStatus(userActivityStatus);

              /**
               * To be an active page group user (i.e. for the student to have a blue
               * draft icon next to their name), they must:
               * 1. Be a "ready" user (have clicked the Join -> Start from student self-select,
               *  or have been placed in a group by the instructor)
               * 2. Be online
               * 3. Be on the group page */
              const isPageGroupUserActive =
                pageGroupUser.is_ready &&
                userActivePageId === pageGroup.page_id &&
                isUserOnline(userActivityType);

              return (
                <DragAndDrop
                  className="w-full"
                  dragItemKind="section_user"
                  dropContainerId={pageGroup.id}
                  item={pageGroupUser}
                  key={pageGroupUser.id}
                  draggable
                  onDropItem={onDropSectionUser}
                >
                  <ListItem
                    className={clsx(
                      "flex w-full items-center justify-between",
                      isPageGroupUserActive && "bg-blue-tint-95",
                    )}
                    data-section-user-id={pageGroupUser.section_user_id}
                    data-testid={`row-${pageGroupUser.section_user_id}`}
                    key={pageGroupUser.id}
                    title={
                      isPageGroupUserActive
                        ? t("group_configuration_card.tooltip.user_is_ready")
                        : t("group_configuration_card.tooltip.user_is_not_ready")
                    }
                    grabbable
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      {userActivityIcons[userActivityType]}
                      <div className="min-w-12 overflow-hidden truncate">
                        {getUserFullName(pageGroupUser)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isPageGroupUserActive ? <LongFormPencilIcon className="text-blue" /> : null}
                      <StudentOptionsMenu
                        currentGroupId={pageGroup.id}
                        page={page}
                        pageGroups={pageGroups}
                        studentSectionUserId={pageGroupUser.section_user_id}
                        isInGroup
                        onRemove={onRemove}
                      />
                    </div>
                  </ListItem>
                </DragAndDrop>
              );
            })
          )}
        </Card>
      </DragAndDrop>
      <ConfirmDeletionDialog
        glossaryKey="group"
        instanceName={t("common.group_title", { number })}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => storeApi.page_groups.destroy(pageGroup.id)}
      >
        <div className="mt-4 border-l-2 border-red pl-4 font-bold">
          {t("confirm_delete_modal.student_content_warning", {
            name: t("common.group_title", { number }),
          })}
        </div>
      </ConfirmDeletionDialog>
    </>
  );
};
